//Global
import React from 'react';

//Local
import Script from 'next/script';
import { getEnvironment } from 'src/lib/services/utils/utils';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';

export const getOneTrustScriptId = (scriptId: string, isProd: boolean): string =>
  isProd || scriptId.endsWith('-test') ? scriptId : `${scriptId}-test`;

export default function OneTrustScript() {
  const siteSettings = useSiteSettings();
  const scriptId = siteSettings?.oneTrustScriptID?.value;

  const disabledFeatures = useDisabledFeatureFlags();

  const isEditing = useIsEditing();
  // Check if scriptId is available
  if (!scriptId) {
    console.warn('OneTrust Script ID not found in Sitecore context');
    return null;
  }

  if (disabledFeatures?.OT) {
    return null;
  }

  if (isEditing) {
    console.log('OneTrust Script is disabled in editing mode');
    return null;
  }
  // Determine environment using GMIServer from .env.local
  const environment = getEnvironment();
  const isProd = environment === 'Production';
  const finalScriptId = getOneTrustScriptId(scriptId, isProd);

  return (
    <>
      {/*
        afterInteractive (not beforeInteractive): the 404 page mounts <Layout> — and this
        component — client-side after hydration. beforeInteractive scripts only run during the
        initial document render, so on error pages the OneTrust SDK would never load, leaving the
        banner, "Customize Cookie Settings" and "Your Privacy Choices" non-functional.
      */}
      <Script
        id="gmi-onetrust-primary-js"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={finalScriptId}
        data-document-language="true"
        strategy={'afterInteractive'}
      />

      <Script id="gmi-onetrust-sdk-wrapper" strategy={'afterInteractive'}>
        {`
          // Outer scope — persists across every OptanonWrapper call made by OneTrust.
          var _gmiOtListenerAttached = false;
          var _gmiOtReloadPending = false;
          // null = no consent action button clicked yet.
          // Set to the current cookie string by the click listener on whitelisted buttons.
          var _gmiOtConsentCookieBeforeAction = null;

          function getConsentCookie() {
            return document.cookie
              .split('; ')
              .find(function(cookie) { return cookie.indexOf('OptanonConsent=') === 0; }) || '';
          }

          function getEnabledConsentGroupsFromCookie(consentCookie) {
            if (!consentCookie) return [];

            var groupsMatch = decodeURIComponent(consentCookie).match(/(?:^|&)groups=([^&]+)/);
            if (!groupsMatch) return [];

            return groupsMatch[1]
              .split(',')
              .filter(function(group) { return group.slice(-2) === ':1'; })
              .map(function(group) { return group.split(':')[0]; })
              .sort();
          }

          function getOneTrustActiveGroups() {
            return String(window.OnetrustActiveGroups || window.OptanonActiveGroups || '')
              .split(',')
              .filter(Boolean)
              .sort();
          }

          function reloadAfterConsentPersists(attempt) {
            var activeGroups = getOneTrustActiveGroups();
            var consentCookie = getConsentCookie();
            var cookieGroups = getEnabledConsentGroupsFromCookie(consentCookie);
            var consentPersisted =
              consentCookie !== _gmiOtConsentCookieBeforeAction &&
              activeGroups.length > 0 &&
              activeGroups.join(',') === cookieGroups.join(',');

            if (consentPersisted || attempt >= 40) {
              location.reload();
              return;
            }

            setTimeout(function() { reloadAfterConsentPersists(attempt + 1); }, 50);
          }

          function scheduleReloadAfterConsentPersists() {
            if (_gmiOtReloadPending) return;
            _gmiOtReloadPending = true;
            reloadAfterConsentPersists(0);
          }

          var CONSENT_ACTION_SELECTORS = [
            'onetrust-accept-btn-handler',   // "Accept All" on banner
            'accept-recommended-btn-handler', // "Accept All" in preference center
            'onetrust-reject-all-handler',   // "Reject All" on banner
            'save-preference-btn-handler'    // "Confirm My Choices" in preference center
          ];
          var CONSENT_ALL_SELECTORS = [
            'onetrust-accept-btn-handler',
            'accept-recommended-btn-handler'
          ];

          // Attach the click listener only once — guard prevents duplicates when
          // OptanonWrapper is called repeatedly.
          if (!_gmiOtListenerAttached) {
            _gmiOtListenerAttached = true;
            document.addEventListener('click', function(event) {
              var el = event.target;
              while (el && el !== document) {
                if (el.classList) {
                  for (var s = 0; s < CONSENT_ACTION_SELECTORS.length; s++) {
                    if (
                      el.id === CONSENT_ACTION_SELECTORS[s] ||
                      el.classList.contains(CONSENT_ACTION_SELECTORS[s])
                    ) {
                      _gmiOtConsentCookieBeforeAction = getConsentCookie();

                      // The preference-center control is commonly named
                      // "accept-recommended" even when the site labels it "Accept All".
                      // Explicitly use OneTrust's all-groups API so every category slider
                      // is enabled before the consent cookie is persisted.
                      for (var a = 0; a < CONSENT_ALL_SELECTORS.length; a++) {
                        if (
                          el.id === CONSENT_ALL_SELECTORS[a] ||
                          el.classList.contains(CONSENT_ALL_SELECTORS[a])
                        ) {
                          setTimeout(function() {
                            if (window.OneTrust && typeof window.OneTrust.AllowAll === 'function') {
                              window.OneTrust.AllowAll();
                            }
                          }, 0);
                          break;
                        }
                      }

                      scheduleReloadAfterConsentPersists();

                      break;
                    }
                  }
                }
                el = el.parentElement;
              }
            }, true);
          }

          // Delegated preference-center opener — bound once on document so it keeps working
          // after Next.js client-side navigation (e.g. leaving a 404) and for dynamically
          // rendered placeholders. Per-element binding only covered links present at SDK init.
          var _gmiOtPreferenceListenerAttached = false;

          function findConsentPreferenceLink(target) {
            var el = target;
            while (el && el !== document) {
              if (
                el.getAttribute &&
                (el.getAttribute('data-gmiot') || '').indexOf('gmi-onetrust-preference-link') !== -1
              ) {
                return el;
              }
              el = el.parentElement;
            }
            return null;
          }

          function openConsentPreferenceCenter(trigger) {
            var ot = window.Optanon || window.OneTrust;
            if (!ot || typeof ot.ToggleInfoDisplay !== 'function') return;
            ot.ToggleInfoDisplay();

            // Return focus to the trigger when the modal closes.
            // Delay lets OneTrust finish rendering #onetrust-pc-sdk first.
            setTimeout(function() {
              var modal = document.querySelector('#onetrust-pc-sdk');
              if (!modal) return;
              var observer = new MutationObserver(function() {
                var isHidden = modal.style.display === 'none' || modal.classList.contains('ot-hide');
                if (isHidden) {
                  if (trigger && typeof trigger.focus === 'function') trigger.focus();
                  observer.disconnect();
                }
              });
              observer.observe(modal, { attributes: true, attributeFilter: ['style', 'class'] });
            }, 1000);
          }

          if (!_gmiOtPreferenceListenerAttached) {
            _gmiOtPreferenceListenerAttached = true;
            document.addEventListener('click', function(event) {
              var link = findConsentPreferenceLink(event.target);
              if (!link) return;
              event.preventDefault();
              openConsentPreferenceCenter(link);
            });
          }

          function OptanonWrapper() {
            // Reload only when a real consent action was taken, never on banner dismiss.
            // OnConsentChanged is registered here because OneTrust is not available
            // in outer scope until after the SDK initialises.
            // _gmiOtConsentCookieBeforeAction is null until a whitelisted button is clicked,
            // so the banner close (X) — which fires OnConsentChanged but never sets the
            // sentinel — will never trigger a reload.
            OneTrust.OnConsentChanged(function() {
              if (_gmiOtConsentCookieBeforeAction !== null) {
                scheduleReloadAfterConsentPersists();
              }
            });

            // Preference-center links (footer + placeholders) are handled by a delegated
            // document listener registered above, so they survive client-side navigation.

            // Make sure the OneTrust banner is hidden from print
            var cookieConsentDiv = document.querySelector('#onetrust-consent-sdk');
            if (cookieConsentDiv != null) {
              cookieConsentDiv.classList.add('printHide');
            }

          }
        `}
      </Script>
    </>
  );
}
